import React from 'react';
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { Github, Linkedin, Mail, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { personalInfo } from '../data/mockData';
import { Button } from './ui/button';

// Framer Motion variants
const containerVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.6,
      ease: 'easeOut',
    },
  }),
};

// LinkedIn headline items
const headlineItems = [
  { icon: '💻', text: personalInfo.title },
  { icon: '', text: 'Full-stack Developer' },
  { icon: '', text: 'Python Developer' },
  { icon: '', text: 'Java Developer' },
  { icon: '🌟', text: "SSOC'4 Contributor" },
  { icon: '🌟', text: "GSSoC'25 Campus Ambassador" },
  { icon: '🌟', text: 'Student Ambassador @LetsUpgrade' },
];

const Hero = () => {
  const navigate = useNavigate();

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
    >
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-72 sm:w-80 md:w-96 h-72 sm:h-80 md:h-96 -top-40 -left-40 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute w-72 sm:w-80 md:w-96 h-72 sm:h-80 md:h-96 -bottom-40 -right-40 bg-blue-500/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          {/* Left Side - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            {/* Open to internships badge */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              custom={0.05}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-500/40 bg-cyan-500/10 px-3 py-1 text-xs sm:text-sm font-medium text-cyan-300 shadow-[0_0_30px_rgba(34,211,238,0.25)]"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
              </span>
              Open to Internships & Job Opportunities
            </motion.div>

            {/* Heading */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 leading-tight whitespace-nowrap">
                Hi, I'm{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  Arpan Jain
                </span>
              </h1>

            </motion.div>

            {/* LinkedIn-style headline badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-2 mb-2 md:mb-4"
            >
              {headlineItems.map((item, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1 bg-gray-800/60 text-gray-200 px-3 py-1 rounded-2xl text-xs sm:text-sm font-medium shadow cursor-default transition-colors duration-200 hover:bg-cyan-700/30"
                >
                  {item.icon && <span>{item.icon}</span>}
                  <span>{item.text}</span>
                </span>
              ))}
            </motion.div>

            {/* Typing animation role line */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl md:text-2xl text-cyan-400 font-semibold"
            >
              <TypeAnimation
                sequence={[
                  'Full-stack Developer',
                  2000,
                  'Python Developer',
                  2000,
                  'Java Developer',
                  2000,
                  'Cloud Enthusiast',
                  2000,
                  'Open Source Contributor',
                  2000,
                ]}
                wrapper="span"
                speed={50}
                repeat={Infinity}
              />
            </motion.div>

            {/* Bio */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-base sm:text-lg text-gray-400 leading-relaxed"
            >
              {personalInfo.bio}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <Button
                onClick={() =>
                  document
                    .getElementById('contact')
                    .scrollIntoView({ behavior: 'smooth' })
                }
                size="lg"
                className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold px-8 py-6 text-base sm:text-lg transition-all"
              >
                Get In Touch
              </Button>
              <Button
                onClick={() => navigate('/resume')}
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 font-semibold px-8 py-6 text-base sm:text-lg transition-all"
              >
                <FileText size={20} className="mr-2" />
                View Resume
              </Button>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="flex gap-4 pt-4 justify-center sm:justify-start"
            >
              <motion.a
                href={personalInfo.github}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.2, rotate: 5 }}
                className="bg-gray-800 p-3 rounded-full hover:bg-cyan-500/20 transition-colors"
              >
                <Github size={24} className="text-cyan-400" />
              </motion.a>
              <motion.a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.2, rotate: 5 }}
                className="bg-gray-800 p-3 rounded-full hover:bg-cyan-500/20 transition-colors"
              >
                <Linkedin size={24} className="text-cyan-400" />
              </motion.a>
              <motion.a
                href={`mailto:${personalInfo.email}`}
                whileHover={{ scale: 1.2, rotate: 5 }}
                className="bg-gray-800 p-3 rounded-full hover:bg-cyan-500/20 transition-colors"
              >
                <Mail size={24} className="text-cyan-400" />
              </motion.a>
            </motion.div>
          </motion.div>

          {/* Right Side - Profile Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex justify-center items-center mt-10 md:mt-0"
          >
            <div className="relative">
              {/* Glowing Border Effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-2xl opacity-60 animate-pulse" />
              {/* Profile Image */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative rounded-full overflow-hidden border-4 border-cyan-400/50 shadow-2xl w-48 h-48 sm:w-56 sm:h-56 lg:w-80 lg:h-80"
              >
                <img
                  src={personalInfo.profileImage}
                  alt="Arpan Jain"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-cyan-400 rounded-full flex items-start justify-center p-2"
          >
            <motion.div className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
